import React from 'react';
import { Box, Stack, TextField, IconButton, Typography, Divider, InputAdornment, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import EditorialButton from '../editorial/EditorialButton';
import { editorialInputSx } from '../editorial/editorialInputStyles';
import { validateHousehold } from '../../service/householdValidation';

const cardSx = {
  p: { xs: 3, md: 4 },
  border: '1px solid rgba(36,34,88,0.15)',
  background: 'var(--paper)',
};

const emptyChild = () => ({ name: '', englishBirthday: '', hebrewBirthday: '' });
const emptyYahrzeit = () => ({ name: '', date: '', relation: '' });

// The household intake form. This FILLS the existing member Firestore
// record (users/{uid}) rather than creating a parallel database — see
// kbt-backend/me/me.controller.js. Runs entirely client-side until
// "Continue" is pressed; validation errors come from
// service/householdValidation.js so the rules are unit-testable
// independent of this component.
//
// Reused as-is (same component, same validation) by MyAccount.js's
// "Household & Family" section for editing this info after enrollment —
// `submitLabel`/`submitting` let that caller relabel the button "Save
// changes" and show a saving state, without changing the signup flow's
// defaults. My Account also passes:
//   - `lockedEmail`: the login email, shown read-only with a note to
//     contact the office. The enrollment wizard leaves this unset so
//     email is collected as a required editable field.
//   - `phoneVerified`: when true, a "Verified" chip is shown on the
//     phone field until the member edits it.
//   - `hiddenFields`: field names already on file (from GET /me/household
//     plus the login identity). Membership 5787 hides those so a signed-in
//     visitor only fills what's missing. My Account leaves this unset.
export default function HouseholdForm({
  household,
  onChange,
  onContinue,
  submitLabel = 'Continue',
  submitting = false,
  lockedEmail = null,
  phoneVerified = false,
  hiddenFields = [],
}) {
  const [errors, setErrors] = React.useState({});
  const [phoneDirty, setPhoneDirty] = React.useState(false);

  const setField = (field) => (e) => onChange({ ...household, [field]: e.target.value });

  const setPhone = (e) => {
    if (phoneVerified) setPhoneDirty(true);
    setField('phone')(e);
  };

  const effectiveEmail = lockedEmail || household.email;
  const showVerified = phoneVerified && !phoneDirty;

  const updateChild = (index, field, value) => {
    const children = household.children.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    onChange({ ...household, children });
  };
  const addChild = () => onChange({ ...household, children: [...household.children, emptyChild()] });
  const removeChild = (index) =>
    onChange({ ...household, children: household.children.filter((_, i) => i !== index) });

  const updateYahrzeit = (index, field, value) => {
    const yahrzeits = household.yahrzeits.map((y, i) => (i === index ? { ...y, [field]: value } : y));
    onChange({ ...household, yahrzeits });
  };
  const addYahrzeit = () => onChange({ ...household, yahrzeits: [...household.yahrzeits, emptyYahrzeit()] });
  const removeYahrzeit = (index) =>
    onChange({ ...household, yahrzeits: household.yahrzeits.filter((_, i) => i !== index) });

  const handleContinue = () => {
    const payload = lockedEmail ? { ...household, email: lockedEmail } : household;
    if (lockedEmail && household.email !== lockedEmail) onChange(payload);
    const { valid, errors: nextErrors } = validateHousehold(payload);
    setErrors(nextErrors);
    if (valid) onContinue();
  };

  const childError = (index, field) => errors.children?.[index]?.[field];
  const yahrzeitError = (index, field) => errors.yahrzeits?.[index]?.[field];
  const hidden = (field) => Array.isArray(hiddenFields) && hiddenFields.includes(field);

  const showHouseholdName = !hidden('householdName');
  const showPrimaryEnglish = !hidden('primaryEnglishBirthday');
  const showPrimaryHebrew = !hidden('primaryHebrewBirthday');
  const showSpouseName = !hidden('spouseName');
  const showSpouseEnglish = !hidden('spouseEnglishBirthday');
  const showSpouseHebrew = !hidden('spouseHebrewBirthday');
  const showAnniversary = !hidden('anniversary');
  const showSpouseBirthdays =
    Boolean(household.spouseName?.trim()) && (showSpouseEnglish || showSpouseHebrew);
  const showHouseholdSection =
    showHouseholdName ||
    showPrimaryEnglish ||
    showPrimaryHebrew ||
    showSpouseName ||
    showSpouseBirthdays ||
    showAnniversary;

  const showPhone = !hidden('phone');
  const showEmail = !hidden('email');
  const showAddress = !hidden('address');
  const showCity = !hidden('city');
  const showState = !hidden('state');
  const showZip = !hidden('zip');
  const showCityRow = showCity || showState || showZip;
  const showContactSection = showPhone || showEmail || showAddress || showCityRow;

  const showChildren = !hidden('children');
  const showYahrzeits = !hidden('yahrzeits');
  const anythingVisible =
    showHouseholdSection || showContactSection || showChildren || showYahrzeits;

  return (
    <Box sx={cardSx} maxWidth={840} mx="auto">
      <Stack spacing={4}>
        {hiddenFields.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {anythingVisible
              ? "We filled in what we already have from your account. Add anything that's still missing."
              : "We've got your household details from your account."}
          </Typography>
        )}

        {showHouseholdSection && (
        <Stack spacing={2.5}>
          <Typography variant="overline" color="text.secondary">
            Household
          </Typography>
          {showHouseholdName && (
          <TextField
            label="Household name"
            value={household.householdName}
            onChange={setField('householdName')}
            error={Boolean(errors.householdName)}
            helperText={errors.householdName || ' '}
            sx={editorialInputSx}
            fullWidth
            required
          />
          )}

          {(showPrimaryEnglish || showPrimaryHebrew) && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {showPrimaryEnglish && (
            <TextField
              label="Your English birthday"
              type="date"
              value={household.primaryEnglishBirthday}
              onChange={setField('primaryEnglishBirthday')}
              error={Boolean(errors.primaryEnglishBirthday)}
              helperText={errors.primaryEnglishBirthday || ' '}
              InputLabelProps={{ shrink: true }}
              sx={editorialInputSx}
              fullWidth
              required
            />
            )}
            {showPrimaryHebrew && (
            <TextField
              label="Your Hebrew birthday"
              placeholder="e.g. 13 Nissan"
              value={household.primaryHebrewBirthday}
              onChange={setField('primaryHebrewBirthday')}
              error={Boolean(errors.primaryHebrewBirthday)}
              helperText={errors.primaryHebrewBirthday || ' '}
              sx={editorialInputSx}
              fullWidth
            />
            )}
          </Stack>
          )}

          {showSpouseName && (
          <TextField
            label="Spouse"
            value={household.spouseName}
            onChange={setField('spouseName')}
            sx={editorialInputSx}
            fullWidth
          />
          )}

          {showSpouseBirthdays && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {showSpouseEnglish && (
              <TextField
                label="Spouse English birthday"
                type="date"
                value={household.spouseEnglishBirthday}
                onChange={setField('spouseEnglishBirthday')}
                error={Boolean(errors.spouseEnglishBirthday)}
                helperText={errors.spouseEnglishBirthday || ' '}
                InputLabelProps={{ shrink: true }}
                sx={editorialInputSx}
                fullWidth
              />
              )}
              {showSpouseHebrew && (
              <TextField
                label="Spouse Hebrew birthday"
                placeholder="e.g. 13 Nissan"
                value={household.spouseHebrewBirthday}
                onChange={setField('spouseHebrewBirthday')}
                error={Boolean(errors.spouseHebrewBirthday)}
                helperText={errors.spouseHebrewBirthday || ' '}
                sx={editorialInputSx}
                fullWidth
              />
              )}
            </Stack>
          )}

          {showAnniversary && (
          <TextField
            label="Anniversary"
            type="date"
            value={household.anniversary}
            onChange={setField('anniversary')}
            error={Boolean(errors.anniversary)}
            helperText={errors.anniversary || ' '}
            InputLabelProps={{ shrink: true }}
            sx={editorialInputSx}
            fullWidth
          />
          )}
        </Stack>
        )}

        {showHouseholdSection && showContactSection && <Divider />}

        {showContactSection && (
        <Stack spacing={2.5}>
          <Typography variant="overline" color="text.secondary">
            Contact
          </Typography>
          {showPhone && (
          <TextField
            label="Phone"
            value={household.phone}
            onChange={setPhone}
            error={Boolean(errors.phone)}
            helperText={
              errors.phone ||
              (phoneVerified && phoneDirty
                ? 'Saving a new number will require re-verification.'
                : ' ')
            }
            InputProps={
              showVerified
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Chip label="Verified" size="small" />
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
            sx={editorialInputSx}
            fullWidth
            required
          />
          )}
          {showEmail && (
          <TextField
            label="Email"
            value={effectiveEmail}
            onChange={lockedEmail ? () => {} : setField('email')}
            error={Boolean(errors.email)}
            helperText={
              errors.email ||
              (lockedEmail
                ? 'This is your login email — contact office@kbtla.org to change it.'
                : ' ')
            }
            InputProps={lockedEmail ? { readOnly: true } : undefined}
            sx={editorialInputSx}
            fullWidth
            required
          />
          )}
          {showAddress && (
          <TextField
            label="Address"
            value={household.address}
            onChange={setField('address')}
            error={Boolean(errors.address)}
            helperText={errors.address || ' '}
            sx={editorialInputSx}
            fullWidth
            required
          />
          )}
          {showCityRow && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {showCity && (
            <TextField
              label="City"
              value={household.city}
              onChange={setField('city')}
              error={Boolean(errors.city)}
              helperText={errors.city || ' '}
              sx={editorialInputSx}
              fullWidth
              required
            />
            )}
            {showState && (
            <TextField
              label="State"
              value={household.state}
              onChange={setField('state')}
              error={Boolean(errors.state)}
              helperText={errors.state || ' '}
              sx={{ ...editorialInputSx, minWidth: { sm: 120 } }}
              required
            />
            )}
            {showZip && (
            <TextField
              label="ZIP code"
              value={household.zip}
              onChange={setField('zip')}
              error={Boolean(errors.zip)}
              helperText={errors.zip || ' '}
              sx={{ ...editorialInputSx, minWidth: { sm: 140 } }}
              required
            />
            )}
          </Stack>
          )}
        </Stack>
        )}

        {(showHouseholdSection || showContactSection) && showChildren && <Divider />}

        {showChildren && (
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" color="text.secondary">
              Children
            </Typography>
            <EditorialButton variant="ghost" onClick={addChild} sx={{ py: 0.8, px: 2 }}>
              <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add child
            </EditorialButton>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
            This helps us plan and grow our children's and youth programming for your family.
          </Typography>

          {household.children.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No children added yet.
            </Typography>
          )}

          {household.children.map((child, index) => (
            <Stack
              key={index}
              spacing={2}
              sx={{ p: 2.5, border: '1px solid rgba(36,34,88,0.1)' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="body2" fontWeight={700}>
                  Child {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeChild(index)}
                  aria-label="Remove child"
                  sx={{ width: 32, minWidth: 32 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                label="Name"
                value={child.name}
                onChange={(e) => updateChild(index, 'name', e.target.value)}
                error={Boolean(childError(index, 'name'))}
                helperText={childError(index, 'name') || ' '}
                sx={editorialInputSx}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="English birthday"
                  type="date"
                  value={child.englishBirthday}
                  onChange={(e) => updateChild(index, 'englishBirthday', e.target.value)}
                  error={Boolean(childError(index, 'englishBirthday'))}
                  helperText={childError(index, 'englishBirthday') || ' '}
                  InputLabelProps={{ shrink: true }}
                  sx={editorialInputSx}
                  fullWidth
                />
                <TextField
                  label="Hebrew birthday"
                  placeholder="e.g. 13 Nissan"
                  value={child.hebrewBirthday}
                  onChange={(e) => updateChild(index, 'hebrewBirthday', e.target.value)}
                  error={Boolean(childError(index, 'hebrewBirthday'))}
                  helperText={childError(index, 'hebrewBirthday') || ' '}
                  sx={editorialInputSx}
                  fullWidth
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
        )}

        {(showHouseholdSection || showContactSection || showChildren) && showYahrzeits && <Divider />}

        {showYahrzeits && (
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" color="text.secondary">
              Yahrzeits
            </Typography>
            <EditorialButton variant="ghost" onClick={addYahrzeit} sx={{ py: 0.8, px: 2 }}>
              <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add yahrzeit
            </EditorialButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            To foster the feeling of community and comradery amongst our members.
          </Typography>

          {household.yahrzeits.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No yahrzeits added yet.
            </Typography>
          )}

          {household.yahrzeits.map((y, index) => (
            <Stack
              key={index}
              spacing={2}
              sx={{ p: 2.5, border: '1px solid rgba(36,34,88,0.1)' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="body2" fontWeight={700}>
                  Yahrzeit {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeYahrzeit(index)}
                  aria-label="Remove yahrzeit"
                  sx={{ width: 32, minWidth: 32 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Name"
                  value={y.name}
                  onChange={(e) => updateYahrzeit(index, 'name', e.target.value)}
                  error={Boolean(yahrzeitError(index, 'name'))}
                  helperText={yahrzeitError(index, 'name') || ' '}
                  sx={editorialInputSx}
                  fullWidth
                />
                <TextField
                  label="Date"
                  placeholder="e.g. 10 Tevet"
                  value={y.date}
                  onChange={(e) => updateYahrzeit(index, 'date', e.target.value)}
                  error={Boolean(yahrzeitError(index, 'date'))}
                  helperText={yahrzeitError(index, 'date') || ' '}
                  sx={editorialInputSx}
                  fullWidth
                />
                <TextField
                  label="Relation"
                  placeholder="e.g. Father"
                  value={y.relation}
                  onChange={(e) => updateYahrzeit(index, 'relation', e.target.value)}
                  error={Boolean(yahrzeitError(index, 'relation'))}
                  helperText={yahrzeitError(index, 'relation') || ' '}
                  sx={editorialInputSx}
                  fullWidth
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
        )}

        <EditorialButton variant="primary" onClick={handleContinue} fullWidth disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </EditorialButton>
      </Stack>
    </Box>
  );
}
